import { Box, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import backgroundPhoto from '../../../assets/images/homeImage.png';
import {
    ARCHIVED_STATUS,
    CONVERT_STATUS,
    CONVERTING_STATUS,
    NOT_PUBLISHED,
    UPLOADING_STATUS
} from '../../../constants';
import {
    CONNECT_PRICE_CHANGE,
    CREATE_EVENT,
    DELETE_DRAFT_EVENT,
    UPDATE_DRAFT_EVENT,
    UPDATE_EVENT
} from '../../../constants/actionTypes';
import fetchData from '../../../fetch';
import colors from '../../../utils/colors';
import { fromUnitAmount } from '../../../utils/unitAmountHelper';
import { EventTypes } from '../../Profile/Streaming/Helpers/eventTypes';
import styles from './home.module.scss';
import SetPrice from '../SetPrice';
import Share from '../Share';
import UploadContent from '../UploadContent';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import AddIcon from '@mui/icons-material/Add';
import sanity from '../../../sanity';
import countryService from 'iso-country-currency';
import { expressCountries } from '../../../constants/express';
import CardsStack from './components/CardsStack';
import { CARDS_KEYS_MAP } from './constants';
import { getCurrencyByCountry } from '../utils';
import { formatValueWithTwoDecimals } from '../../../utils/number-format';
import { ActionIcon } from '../../../core/components';
import NavMobile from '../../Nav/NavMobile';
import NavDesktop from '../../Nav/NavDesktop';
import theme from '../../../theme';
import { getRandomRightSectionImageSrc } from '../../../utils/images';

const mapStateToProps = (state) => {
    return {
        ...state.liveStreaming,
        user: state.user.currentUser,
        upload: state.common.upload,
        draftEvent: state.common.draftEvent,
        connected_account: state.common.connected_account,
        connected_products: state.common.connected_products,
        connected_prices: state.prices.connected_prices,
        events: state.event.events
    };
};
const mapDispatchToProps = (dispatch) => ({
    onNewEvent: (payload) => dispatch({ type: CREATE_EVENT, payload }),
    onUpdateEvent: (payload) => dispatch({ type: UPDATE_EVENT, payload }),
    onPriceChange: (payload) => dispatch({ type: CONNECT_PRICE_CHANGE, payload }),
    onUpdateDraftEvent: (payload) => dispatch({ type: UPDATE_DRAFT_EVENT, payload }),
    onDeleteDraftEvent: (payload) => dispatch({ type: DELETE_DRAFT_EVENT, payload })
});

const Home = (props) => {
    const [selectedCard, setSelectedCard] = useState(CARDS_KEYS_MAP.UPLOAD);
    const date = new Date();
    const [now, setNow] = useState();
    const [title, setTitle] = useState(props.draftEvent.title || '');
    const [description, setDescription] = useState(props.draftEvent.description || '');
    const [minimumPrice, setMinimumPrice] = useState(props.draftEvent.minimumPrice || 0);
    const [price, setPrice] = useState(props.draftEvent.price || '');
    const [titleError, setTitleError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [priceError, setPriceError] = useState('');
    const { t } = useTranslation();
    const eventTypes = EventTypes();
    const [thumbnail, setThumbnail] = useState(props.draftEvent.thumbnail);
    const [imageUrl, setImageUrl] = useState(props.draftEvent.imageUrl);
    const [eventType, setEventType] = useState('video_event');
    const [streamType, setStreamType] = useState('url_pull');
    const [videoFile, setVideoFile] = useState(props.draftEvent.videoFile);
    const [videoDuration, setVideoDuration] = useState(null);
    const [eventProducts, setEventProducts] = useState([]);
    const [jobId, setJobId] = useState(props.draftEvent.jobId || uuidv4());
    const [fileUpload, setFileUpload] = useState(props.draftEvent.fileUpload || false);
    const [fileUploadError, setFileUploadError] = useState('');
    const [defaultPrice, setDefaultPrice] = useState({});
    const [upload, setUpload] = useState();
    const [loading, setLoading] = useState(false);
    const [videoOrientation, setVideoOrientation] = useState('landscape');
    const [finishedFlag, setFinishedFlag] = useState(false);
    const location = useLocation();
    const hasBackground = location.pathname === '/home';
    const [linkCreated, setLinkCreated] = useState(false);
    const [hasTrailer, setHasTrailer] = useState(props.draftEvent.hasTrailer || false);
    const [showOnboardingMessage, setShowOnboardingMessage] = useState();
    const [publishingVideos, setPublishingVideos] = useState(null);
    const [shareCardTitle, setShareCardTitle] = useState(t('CreateLink'));
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));

    useEffect(() => {
        const fetchData = async () => {
            let events = await sanity.Events.GetEvents(props.isAdmin);
            const publishingEvents = events
                .filter(
                    (event) =>
                        event.processingStatus === CONVERTING_STATUS ||
                        event.processingStatus === UPLOADING_STATUS ||
                        event.processingStatus === CONVERT_STATUS ||
                        (event.stream_status !== ARCHIVED_STATUS &&
                            event.stream_status !== NOT_PUBLISHED)
                )
                .filter((e) => e.processingStatus !== undefined);
            setPublishingVideos([...publishingEvents]);
        };
        fetchData();
    }, [props.events]);

    useEffect(() => {
        let defaultValues = {};
        let showPriceField = {};
        if (props.connected_prices && props.connected_prices.length > 0) {
            for (const eventProduct of eventProducts) {
                showPriceField = { ...showPriceField, [eventProduct.id]: false };
                defaultValues[eventProduct.id] = props.connected_prices
                    .filter((p) => p.product === eventProduct.id)
                    .map((p) => {
                        fromUnitAmount(p.price.unit_amount, p.price.currency);
                    })
                    .pop();
            }
        }
        const price = [{ ...defaultValues }];
        setDefaultPrice(price);
    }, [props.connected_prices, eventProducts, finishedFlag]);

    useEffect(() => {
        if (jobId && props.upload) {
            const upload = props.upload.find((x) => x.jobId === jobId);
            setUpload(upload);
        }
    }, [props.upload, jobId]);

    useEffect(() => {
        if (props.connected_products && props.connected_products.length > 0) {
            setEventProducts(
                props.connected_products.filter((ca) => ca.metadata['setup-price'] === 'event')
            );
        }
    }, [props.connected_products, finishedFlag]);

    useEffect(() => {
        if (finishedFlag) {
            setFileUpload(null);
            setTitle('');
            setVideoFile(null);
            setDescription('');
            setPrice('');
            setMinimumPrice(0);
            setNow(new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString());
        }
    }, [finishedFlag]);

    useEffect(() => {
        if (jobId) {
            props.onUpdateDraftEvent({ jobId });
        }
    }, []);

    const createEvent = async () => {
        const formattedPrice = price.replace(',', '.');

        const startDate = dayjs(undefined).valueOf() ?? now;
        let endDate = null;
        if (videoDuration && !isNaN(videoDuration) && startDate) {
            endDate = new Date(new Date(startDate).getTime() + Math.ceil(videoDuration) * 1000);
        }
        if (startDate < now) {
            // presentToast('Please select date in the future', closeCircleSharp, 'danger', 1500);
        } else {
            setLoading(true);
            try {
                const _eType = eventTypes.find((et) => et.value === eventType);
                const _resource_creation_time = 1.5;
                let productId;
                const product = {
                    name: title,
                    description: description,
                    // caption: description,
                    metadata: {
                        'sub-type': 'pay-per-view',
                        interval: 'single-charge',
                        type: 'one-time',
                        'setup-price': 'event'
                    },
                    images: imageUrl ? [imageUrl] : []
                };

                let currency = '';
                countryService
                    .getAllISOCodes()
                    .filter((c) => expressCountries.some((v) => v.iso === c.iso))
                    .map((t) => {
                        if (t.iso === props?.connected_account?.country) currency = t.currency;
                    });
                if (formattedPrice) {
                    const newPrice = await fetchData.Prices.Create(
                        product,
                        undefined,
                        formattedPrice,
                        currency
                    );
                    productId = newPrice.product;
                    props.onPriceChange({ product: product, price: parseInt(formattedPrice) });
                }

                const eventData = {
                    title: title,
                    description: description,
                    slug: {
                        type: 'slug',
                        current: uuidv4()
                    },
                    startDate: new Date(startDate),
                    endDate: new Date(endDate),
                    event_type: _eType.value,
                    stream_type: streamType,
                    streamStatus: CONVERT_STATUS,
                    user_id: props.user.data.sub,
                    tags: [_eType.title],
                    thumbnail: thumbnail,
                    correlation_id: jobId,
                    endpoint: upload?.upload_url,
                    trailer: upload?.trailer_url,
                    endpointRef: {
                        _ref: upload?.upload_reference?._id,
                        _type: 'reference'
                    },
                    trailerRef: {
                        _ref: upload?.trailer_reference?._id,
                        _type: 'reference'
                    },
                    resource_creation_time: _resource_creation_time,
                    pay_per_view_product: productId,
                    // pay_per_view_price_amount: formattedPrice,
                    minimum_calculated_price: minimumPrice,
                    // pay_per_view_price_currency: currency,
                    subscriber_exclusive: true,
                    sports_team: {
                        _ref: process.env.REACT_APP_COMPANY_ID,
                        _type: 'reference'
                    },
                    convert_start_dates: [],
                    convert_end_dates: [],
                    convert_mode: videoOrientation
                };
                props.onNewEvent({ ...eventData });
                //props.onUpdateEvent({ jobId, ...eventData });
                props.onDeleteDraftEvent();
                // props.setScheduleEventOpen
                //     ? props.setScheduleEventOpen(false)
                //     : setSelectedCard(3);
                // setFinishedFlag(!finishedFlag);
                return eventData.correlation_id;
            } catch (e) {
                console.error(e.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const validateFields = () => {
        if (!fileUpload) {
            setFileUploadError(t('RequiredVideo'));
            setSelectedCard(CARDS_KEYS_MAP.UPLOAD);
            return false;
        }

        if (streamType === 'url_pull' && !fileUpload) {
            setFileUploadError(t('RequiredVideo'));
            setSelectedCard(CARDS_KEYS_MAP.UPLOAD);
            return false;
        }
        if (title.trim() === '') {
            setTitleError(t('RequiredTitle'));
            setSelectedCard(CARDS_KEYS_MAP.UPLOAD);
            return false;
        }
        if (description.trim() === '') {
            setDescriptionError(t('RequiredDescription'));
            setSelectedCard(CARDS_KEYS_MAP.UPLOAD);
            return false;
        }

        const formattedPrice = price.replace(',', '.');

        if (parseFloat(formattedPrice) < minimumPrice || formattedPrice === '') {
            setPriceError(t('RequiredPrice', { minimumPrice: `${minimumPrice.toString()}€` }));
            setSelectedCard(CARDS_KEYS_MAP.SET_PRICE);
            return false;
        }

        return true;
    };

    const handleCardClick = (cardKey) => {
        if (cardKey === CARDS_KEYS_MAP.CREATE_LINK && !validateFields()) {
            return;
        }

        setSelectedCard(cardKey);
    };

    const firstCardClosedTitle = fileUpload
        ? upload?.upload_show
            ? t('Uploading')
            : t('UploadComplete')
        : t('Upload');

    const currency = useMemo(
        () => getCurrencyByCountry(props?.connected_account?.country)?.toUpperCase(),
        [props?.connected_account?.country]
    );

    const getCards = () => {
        const cardsParams = [
            {
                key: CARDS_KEYS_MAP.UPLOAD,
                title: t('Upload'),
                closedTitle: firstCardClosedTitle,
                component: (
                    <UploadContent
                        title={title}
                        description={description}
                        setTitle={setTitle}
                        setDescription={setDescription}
                        setMinimumPrice={setMinimumPrice}
                        setThumbnail={setThumbnail}
                        setImageUrl={setImageUrl}
                        fileUploadError={fileUploadError}
                        titleError={titleError}
                        descriptionError={descriptionError}
                        setFileUploadError={setFileUploadError}
                        setTitleError={setTitleError}
                        setDescriptionError={setDescriptionError}
                        setFileUpload={setFileUpload}
                        fileUpload={fileUpload}
                        videoFile={videoFile}
                        setVideoFile={setVideoFile}
                        setVideoOrientation={setVideoOrientation}
                        setVideoDuration={setVideoDuration}
                        jobId={jobId}
                        setHasTrailer={setHasTrailer}
                    />
                ),
                backgroundColor: colors.primary,
                textColor: 'black'
            },
            {
                key: CARDS_KEYS_MAP.SET_PRICE,
                title: t('SetPrice'),
                closedTitle: price
                    ? `${t('Price')}: ${formatValueWithTwoDecimals(price)} ${currency}`
                    : t('SetPrice'),
                component: (
                    <SetPrice
                        currency={currency}
                        minimumPrice={minimumPrice}
                        price={price}
                        setPrice={setPrice}
                        priceError={priceError}
                        setPriceError={setPriceError}
                    />
                ),
                backgroundColor: colors.secondary,
                textColor: 'black'
            },
            {
                key: CARDS_KEYS_MAP.CREATE_LINK,
                title: shareCardTitle,
                component: (
                    <Share
                        fileUpload={fileUpload}
                        createEvent={createEvent}
                        selectedCard={selectedCard}
                        setSelectedCard={setSelectedCard}
                        videoProgress={upload}
                        setLinkCreated={setLinkCreated}
                        hasTrailer={hasTrailer}
                        showOnboardingMessage={showOnboardingMessage}
                        setShowOnboardingMessage={setShowOnboardingMessage}
                        setFinishedFlag={setFinishedFlag}
                        setJobId={setJobId}
                        setCardTitle={setShareCardTitle}
                    />
                ),
                backgroundColor: '#000000',
                textColor: 'white'
            }
        ];

        return selectedCard === CARDS_KEYS_MAP.CREATE_LINK && !showOnboardingMessage
            ? [cardsParams[2]]
            : cardsParams;
    };

    const handleNewUploadStart = () => {
        setLinkCreated(false);
        setJobId(uuidv4());
        setFinishedFlag(!finishedFlag);
        setSelectedCard(CARDS_KEYS_MAP.UPLOAD);
        setTitle('');
        setDescription('');
        setPrice('');
        setVideoFile(null);
        props.onDeleteDraftEvent();
    };

    const backgroundPhotoSrc = useMemo(() => getRandomRightSectionImageSrc(), []);

    return (
        <Box className={styles.root}>
            {isMobile ? (
                <NavMobile publishingVideos={publishingVideos} />
            ) : (
                <NavDesktop publishingVideos={publishingVideos} hasBackground={hasBackground} />
            )}
            <Box className={styles.content}>
                <Box className={styles.mainSection}>
                    <Box className={styles.cardsWrapper}>
                        <Box>
                            <CardsStack
                                onCardClick={handleCardClick}
                                selectedCard={selectedCard}
                                cards={getCards()}
                            />
                            {selectedCard === CARDS_KEYS_MAP.CREATE_LINK &&
                                !showOnboardingMessage && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            marginTop: '3rem'
                                        }}>
                                        <ActionIcon
                                            Icon={AddIcon}
                                            title={t('StartNewUpload')}
                                            onClick={handleNewUploadStart}
                                        />
                                    </Box>
                                )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box className={styles.imageSection}>
                <img className={styles.image} src={backgroundPhotoSrc} alt="home-image" />
            </Box>
        </Box>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
